import React, { useState, useEffect, useRef, BaseSyntheticEvent } from "react";
import {
  useUserDataContext,
  itemDataFromDynamo,
  cFRow,
} from "../../contexts/UserDataContext";
import { useAccountContext, cognitoUserAttributes } from "../../contexts/Account";
import {
  StyledInnerPopup,
  StyledOuterPopup,
} from "../../styles/components/Popup.styled";
import { useScrollLock } from "../../hooks";
import {
  StyledIS_Wrapper,
  StyledItemSubmissionForm,
  StyledItemSubmissionFormLabel,
  StyledItemSubmissionFormSpan,
  StyledItemSubmissionFormInput,
  StyledItemSubmissionFormTextArea,
  StyledItemSubmissionImageDiv,
  StyledItemSubmissionCenteredDiv,
  StyledItemSubmissionCFContainer,
  StyledItemSubmissionCFItem,
  StyledItemSubmissionCFData,
  StyledItemSubmissionCFDeleteBtnContainer,
  StyledISTrashButton,
  StyledIS_ButtonContainer,
} from "../../pages/MyItems/ItemSubmission.styled";
import {
  StyledIS_CardButton,
} from "../../styles/components/Buttons.styled";
import TagsInput from "./TagsInput";
import ImageModal from "../common/ImageModal";
import { Trash, PlusSquare } from "react-bootstrap-icons";

export function BulkWorkflowFormPopup() {
  const {
    setupgradedItemFormOpen,
    curItemArray,
    setBulkWorkflow,
    setParentToBe,
    setCurItemData,
  } = useUserDataContext();

  // Lock scrolling when modal is open
  useScrollLock(true);

  useEffect(() => {
    if (curItemArray.length > 0) {
      setParentToBe(curItemArray[0].ParentId);
      setCurItemData(curItemArray[0]);
    }
  }, []);

  return (
    <StyledOuterPopup>
      <StyledInnerPopup>
        <BulkWorkflowForm />
      </StyledInnerPopup>
    </StyledOuterPopup>
  );
}

export function BulkWorkflowForm() {
  const {
    curItemArray,
    itemMap,
    SaveItem,
    setupgradedItemFormOpen,
    setBulkWorkflow,
    setIsLoading,
  } = useUserDataContext();

  const { userCognitoAttrubutes } = useAccountContext();

  // Navigation state - store ItemIds in original order to prevent reordering issues
  const [itemIdsInOrder, setItemIdsInOrder] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [customFields, setCustomFields] = useState<cFRow[]>([]);

  // Original values for change detection
  const [originalName, setOriginalName] = useState("");
  const [originalDescription, setOriginalDescription] = useState("");
  const [originalQuantity, setOriginalQuantity] = useState("");
  const [originalTags, setOriginalTags] = useState<string[]>([]);
  const [originalCustomFields, setOriginalCustomFields] = useState<cFRow[]>([]);

  // Error state
  const [errors, setErrors] = useState<string[]>([]);

  // Auto-focus ref for custom fields
  const cfInputRef = useRef<HTMLInputElement>(null);
  const [doSetAutoFocus, setDoSetAutoFocus] = useState(false);

  // Initialize item order on mount
  useEffect(() => {
    if (curItemArray.length > 0 && itemIdsInOrder.length === 0) {
      const ids = curItemArray.map(item => item.ItemId);
      setItemIdsInOrder(ids);
    }
  }, [curItemArray]);

  // Current item - lookup by ItemId to handle array reordering
  const currentItemId = itemIdsInOrder[currentIndex];
  const currentItem = currentItemId ? itemMap[currentItemId] : null;

  // Load current item data into form
  useEffect(() => {
    if (currentItem) {
      const itemName = currentItem.Name || "";
      const itemDesc = currentItem.Desc || "";
      const itemQty = currentItem.Quantity || "";
      const itemTags = currentItem.Tags || [];
      const itemCF = currentItem.CustomFields || [];

      // Set form values
      setName(itemName);
      setDescription(itemDesc);
      setQuantity(itemQty);
      setTags(itemTags);
      setCustomFields(itemCF);

      // Store original values for change detection
      setOriginalName(itemName);
      setOriginalDescription(itemDesc);
      setOriginalQuantity(itemQty);
      setOriginalTags(itemTags);
      setOriginalCustomFields(itemCF);

      setErrors([]);
    }
  }, [currentIndex, currentItem]);

  // Auto-focus for custom fields
  useEffect(() => {
    if (doSetAutoFocus && cfInputRef.current) {
      window.setTimeout(() => {
        cfInputRef.current?.focus();
      }, 0);
      setDoSetAutoFocus(false);
    }
  }, [doSetAutoFocus]);

  // Check if any changes have been made
  const hasUnsavedChanges = (): boolean => {
    // Check name
    if (name !== originalName) return true;

    // Check description
    if (description !== originalDescription) return true;

    // Check quantity
    if (quantity !== originalQuantity) return true;

    // Check tags (compare arrays)
    if (tags.length !== originalTags.length) return true;
    if (!tags.every((tag, index) => tag === originalTags[index])) return true;

    // Check custom fields (compare arrays of objects)
    if (customFields.length !== originalCustomFields.length) return true;
    for (let i = 0; i < customFields.length; i++) {
      const cf = customFields[i];
      const origCf = originalCustomFields[i];
      if (
        cf.id !== origCf.id ||
        cf.key !== origCf.key ||
        cf.value !== origCf.value
      ) {
        return true;
      }
    }

    return false;
  };

  // Validation
  const validateForm = (): string[] => {
    const errorArray: string[] = [];

    // Name is required
    if (!name || name.trim() === "") {
      errorArray.push("Item name is required.");
    }

    // Validate quantity is a number if provided
    if (quantity !== "" && isNaN(parseInt(quantity))) {
      errorArray.push("Quantity must be a number.");
    }

    // Validate custom fields have both key and value
    customFields.forEach((row) => {
      if (row.key === null || row.value === null || row.key === "" || row.value === "") {
        errorArray.push(
          `Custom field row ${row.id} must have both a key and value.`
        );
      }
    });

    return errorArray;
  };

  // Navigation handlers
  const goToNext = () => {
    if (currentIndex < itemIdsInOrder.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goToPrev = () => {
    if (currentIndex > 0) {
      // Check for unsaved changes
      if (hasUnsavedChanges()) {
        const confirmed = window.confirm(
          "You have unsaved changes. Are you sure you want to go to the previous item? Your changes will be lost."
        );
        if (!confirmed) {
          return;
        }
      }
      setCurrentIndex(currentIndex - 1);
    }
  };

  const skipItem = () => {
    // Check for unsaved changes
    if (hasUnsavedChanges()) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to skip this item? Your changes will be lost."
      );
      if (!confirmed) {
        return;
      }
    }

    if (currentIndex < itemIdsInOrder.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Last item - close the form (skip confirmation since user already confirmed above if needed)
      handleClose(true);
    }
  };

  // Save handler
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // Safety check
    if (!currentItem) {
      return;
    }

    // Validate
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    setIsLoading("Saving...");

    // Build updated item data
    const updatedItem: itemDataFromDynamo = {
      ItemId: currentItem.ItemId,
      ParentId: currentItem.ParentId,
      InUse: currentItem.InUse,
      Image: currentItem.Image,
      Name: name,
      Desc: description,
      Quantity: quantity,
      Tags: tags,
      CustomFields: customFields,
    };

    try {
      await SaveItem(
        updatedItem,
        currentItem.ItemId,
        false, // Don't close
        "", // No image upload in bulk workflow
        (userCognitoAttrubutes as cognitoUserAttributes).sub!,
        true // Recalculate current array
      );

      // Move to next item or close if last
      if (currentIndex < itemIdsInOrder.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        // Last item saved - close the form without confirmation
        handleClose(true);
      }
    } catch (error) {
      console.error("Error saving item in bulk workflow:", error);
      alert(
        "Error saving item. Please check your connection and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Close handler
  const handleClose = (skipConfirmation: boolean = false) => {
    // Check for unsaved changes (unless explicitly skipping confirmation)
    if (!skipConfirmation && hasUnsavedChanges()) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to close? Your changes will be lost."
      );
      if (!confirmed) {
        return;
      }
    }

    setupgradedItemFormOpen(false);
    setBulkWorkflow(false);
  };

  // Custom field handlers
  const addCustomFieldRow = () => {
    const newId =
      customFields.length > 0
        ? customFields[customFields.length - 1].id + 1
        : 1;
    setCustomFields([...customFields, { id: newId, key: "", value: "" }]);
    setDoSetAutoFocus(true);
  };

  const deleteCustomFieldRow = (id: number) => {
    setCustomFields(customFields.filter((row) => row.id !== id));
  };

  const updateCustomFieldKey = (id: number, value: string) => {
    setCustomFields(
      customFields.map((row) =>
        row.id === id ? { ...row, key: value === "" ? null : value } : row
      )
    );
  };

  const updateCustomFieldValue = (id: number, value: string) => {
    setCustomFields(
      customFields.map((row) =>
        row.id === id ? { ...row, value: value === "" ? null : value } : row
      )
    );
  };

  if (!currentItem || itemIdsInOrder.length === 0) {
    return (
      <StyledIS_Wrapper>
        <div>No items to edit</div>
        <StyledIS_ButtonContainer>
          <StyledIS_CardButton onClick={() => handleClose(true)}>
            Close
          </StyledIS_CardButton>
        </StyledIS_ButtonContainer>
      </StyledIS_Wrapper>
    );
  }

  return (
    <StyledIS_Wrapper>
      <StyledItemSubmissionForm
        onSubmit={handleSave}
        onKeyDown={(e: BaseSyntheticEvent) => {
          if (e.type === "keydown" && e.target.type !== "textarea") {
            const ke: KeyboardEvent = e.nativeEvent as KeyboardEvent;
            if (ke.key === "Enter") {
              e.preventDefault();
            }
          }
        }}
      >
        {/* Progress Indicator */}
        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "1rem", textAlign: "center" }}>
          Editing Item {currentIndex + 1} of {itemIdsInOrder.length}
        </div>

        {/* Image Display (read-only) */}
        {currentItem.Image && (
          <StyledItemSubmissionImageDiv>
            <StyledItemSubmissionCenteredDiv>
              <ImageModal
                src={currentItem.Image + "_mainimg.jpeg"}
                quantity={currentItem.Quantity}
              />
            </StyledItemSubmissionCenteredDiv>
          </StyledItemSubmissionImageDiv>
        )}

        {/* Name Field */}
        <StyledItemSubmissionFormLabel>
          <StyledItemSubmissionFormSpan>Name *</StyledItemSubmissionFormSpan>
          <StyledItemSubmissionFormInput
            data-lpignore="true"
            type="text"
            name="name"
            placeholder="Item Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            required
          />
        </StyledItemSubmissionFormLabel>

        {/* Description Field */}
        <StyledItemSubmissionFormLabel>
          <StyledItemSubmissionFormSpan>Description</StyledItemSubmissionFormSpan>
          <StyledItemSubmissionFormTextArea
            data-lpignore="true"
            name="desc"
            placeholder="Item Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
          />
        </StyledItemSubmissionFormLabel>

        {/* Quantity Field */}
        <StyledItemSubmissionFormLabel>
          <StyledItemSubmissionFormSpan>Quantity</StyledItemSubmissionFormSpan>
          <StyledItemSubmissionFormInput
            data-lpignore="true"
            type="text"
            name="quantity"
            placeholder="Enter Quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            maxLength={10}
          />
        </StyledItemSubmissionFormLabel>

        {/* Tags Field */}
        <StyledItemSubmissionFormLabel>
          <StyledItemSubmissionFormSpan>Tags</StyledItemSubmissionFormSpan>
          <TagsInput tags={tags} setTags={setTags} />
        </StyledItemSubmissionFormLabel>

        {/* Custom Fields */}
        <StyledItemSubmissionFormLabel>
          <StyledItemSubmissionFormSpan>Custom Fields</StyledItemSubmissionFormSpan>
          <StyledItemSubmissionCFContainer>
            {customFields.length > 0 &&
              customFields.map((cfRow, index) => (
                <StyledItemSubmissionCFItem key={cfRow.id}>
                  <StyledItemSubmissionCFData>
                    <StyledItemSubmissionFormInput
                      data-lpignore="true"
                      type="text"
                      name="customFieldKey"
                      placeholder="Enter Custom Field Key"
                      value={cfRow.key || ""}
                      onChange={(e) =>
                        updateCustomFieldKey(cfRow.id, e.target.value)
                      }
                      ref={index === customFields.length - 1 ? cfInputRef : undefined}
                      maxLength={50}
                    />
                    <StyledItemSubmissionFormTextArea
                      data-lpignore="true"
                      name="customFieldValue"
                      placeholder="Enter Custom Field Value"
                      value={cfRow.value || ""}
                      onChange={(e) =>
                        updateCustomFieldValue(cfRow.id, e.target.value)
                      }
                      maxLength={250}
                    />
                  </StyledItemSubmissionCFData>
                  <StyledItemSubmissionCFDeleteBtnContainer>
                    <StyledISTrashButton
                      type="button"
                      onClick={() => deleteCustomFieldRow(cfRow.id)}
                    >
                      <Trash />
                    </StyledISTrashButton>
                  </StyledItemSubmissionCFDeleteBtnContainer>
                </StyledItemSubmissionCFItem>
              ))}
            <div>
              <PlusSquare onClick={addCustomFieldRow} style={{ cursor: "pointer" }} />
            </div>
          </StyledItemSubmissionCFContainer>
        </StyledItemSubmissionFormLabel>

        {/* Error Messages */}
        {errors.length > 0 &&
          errors.map((error, index) => (
            <div
              key={index}
              style={{ color: "red", margin: "0.5rem 0", fontWeight: "bold" }}
            >
              {error}
            </div>
          ))}

        {/* Action Buttons */}
        <StyledIS_ButtonContainer>
          <StyledIS_ButtonContainer>
            <StyledIS_CardButton
              type="button"
              onClick={goToPrev}
              disabled={currentIndex === 0}
            >
              Previous
            </StyledIS_CardButton>
            <StyledIS_CardButton type="button" onClick={skipItem}>
              Skip
            </StyledIS_CardButton>
            <StyledIS_CardButton type="submit">
              Save & Next
            </StyledIS_CardButton>
          </StyledIS_ButtonContainer>
          <StyledIS_ButtonContainer>
            <StyledIS_CardButton type="button" onClick={() => handleClose()}>
              Close
            </StyledIS_CardButton>
          </StyledIS_ButtonContainer>
        </StyledIS_ButtonContainer>
      </StyledItemSubmissionForm>
    </StyledIS_Wrapper>
  );
}
